import { hash } from "daedalus-core";
import { SEASONS, seasonOfTurn, turnOf, type Season } from "../time/game-clock.ts";
import table from "./weather.json";

/**
 * Regional weather (ADR-144 decisions 1 to 5): what falls on a region in a turn, from the world seed, the region and the
 * game clock alone. Every map server, page and farming evaluator calls the same pure function, so nothing about weather is
 * sent or saved. All arithmetic on drawn numbers is integer, so every engine agrees to the last turn.
 */

/**
 * The kind of weather a storm is.
 */
export const STORM_KIND = "storm";

/**
 * What falls in a region in one turn.
 */
export type WeatherKind = "clear" | "rain" | typeof STORM_KIND;

/**
 * The weather of one region in one turn: its kind and the wetness of the front it belongs to, 0 to 255.
 *
 * `wetness` is a 0 to 255 rescaling of the front's wetness for callers that draw or water by degree. It is rounded down and is
 * not the value a season's `stormFrom` is compared against, so a caller must not compare the two; `kind` already says storm.
 */
export interface Weather {
  readonly kind: WeatherKind;
  readonly wetness: number;
}

/**
 * The odds of one season, whole percents.
 *
 * `rainPercent` is the share of turns that rain, on average. `swingPercent` is how far a front's wetness may stray from it,
 * as a share of the distance to the nearer end (0 or 100): 100 lets fronts run from dry spells to downpours. `stormFrom` is
 * the front wetness, in percent of turns raining, at or above which a raining turn is a storm; 100 means never. It is a point
 * in the range the season's fronts can reach (the mean plus or minus the swing), not an absolute wetness, so a dry season's line
 * sits low. It is not on the 0 to 255 scale of `Weather.wetness`.
 */
export interface SeasonOdds {
  readonly rainPercent: number;
  readonly swingPercent: number;
  readonly stormFrom: number;
}

/**
 * The weather table (`weather.json`, world content): the length of a front in turns and the odds of each season.
 */
export interface WeatherTable {
  readonly frontTurns: number;
  readonly seasons: Readonly<Record<Season, SeasonOdds>>;
}

/**
 * One thing wrong with a weather table.
 */
export interface WeatherProblem {
  readonly field: string;
  readonly message: string;
}

/**
 * The verdict on a weather table.
 */
export type WeatherVerdict = { readonly ok: true; readonly table: WeatherTable } | { readonly ok: false; readonly problems: readonly WeatherProblem[] };

/**
 * The most turns one front may last.
 */
export const MAX_FRONT_TURNS = 48;

const TOP_FIELDS = ["frontTurns", "seasons"] as const;
const ODDS_FIELDS = ["rainPercent", "swingPercent", "stormFrom"] as const;
const UNIT_RANGE = 1 << 24;
const BASIS_POINTS = 10000;
const FRONT_DOMAIN = 1;
const TURN_DOMAIN = 2;
const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/**
 * The weather table the game ships, from `weather.json`.
 */
export const DEFAULT_WEATHER: WeatherTable = Object.freeze(table as WeatherTable);

/**
 * Checks a weather table and names every problem.
 *
 * @param value The parsed `weather.json`.
 * @returns The table when it is sound, else every problem with the field it is in.
 * @example
 * validateWeather(JSON.parse(text));
 */
export function validateWeather(value: unknown): WeatherVerdict {
  const problems: WeatherProblem[] = [];
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { ok: false, problems: [{ field: "", message: "the weather table must be an object" }] };
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(TOP_FIELDS as readonly string[]).includes(key)) problems.push({ field: key, message: "is not a field of the weather table" });
  }
  const front = record.frontTurns;
  if (typeof front !== "number" || !Number.isInteger(front) || front < 1 || front > MAX_FRONT_TURNS) {
    problems.push({ field: "frontTurns", message: `must be a whole number from 1 to ${MAX_FRONT_TURNS}` });
  }
  const seasons = record.seasons;
  if (typeof seasons !== "object" || seasons === null || Array.isArray(seasons)) {
    problems.push({ field: "seasons", message: "must be an object keyed by season" });
  } else {
    const byName = seasons as Record<string, unknown>;
    for (const key of Object.keys(byName)) {
      if (!(SEASONS as readonly string[]).includes(key)) problems.push({ field: `seasons.${key}`, message: "is not a season of the clock" });
    }
    for (const season of SEASONS) {
      checkOdds(byName[season], `seasons.${season}`, problems);
    }
  }
  return problems.length === 0 ? { ok: true, table: value as WeatherTable } : { ok: false, problems };
}

/**
 * Checks one season's odds, adding each problem to the list.
 *
 * @param odds The season's entry, or undefined when missing.
 * @param path The field path of the entry.
 * @param problems The list to add to.
 */
function checkOdds(odds: unknown, path: string, problems: WeatherProblem[]): void {
  if (typeof odds !== "object" || odds === null || Array.isArray(odds)) {
    problems.push({ field: path, message: "must be an object with rainPercent, swingPercent and stormFrom" });
    return;
  }
  const record = odds as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(ODDS_FIELDS as readonly string[]).includes(key)) problems.push({ field: `${path}.${key}`, message: "is not a field of a season's odds" });
  }
  for (const key of ODDS_FIELDS) {
    const entry = record[key];
    if (typeof entry !== "number" || !Number.isInteger(entry) || entry < 0 || entry > 100) {
      problems.push({ field: `${path}.${key}`, message: "must be a whole number from 0 to 100" });
    }
  }
}

/**
 * The region whose weather a map gets: its own region, or the map itself when it has none (ADR-144 decision 3).
 *
 * @param mapId The map's id.
 * @param regionId The map's region (ADR-143), or null.
 * @returns The id weather is drawn for.
 */
export function weatherRegionOf(mapId: string, regionId: string | null): string {
  return regionId ?? mapId;
}

/**
 * Folds a region id into an unsigned 32-bit number (FNV-1a over its UTF-16 units).
 *
 * @param regionId The region id.
 * @returns An unsigned 32-bit digest.
 */
export function regionWord(regionId: string): number {
  let digest = FNV_OFFSET;
  for (let i = 0; i < regionId.length; i += 1) {
    digest = Math.imul(digest ^ regionId.charCodeAt(i), FNV_PRIME) >>> 0;
  }
  return digest;
}

/**
 * The seed every map server, page and farming evaluator draws a world's weather from until worlds carry a seed of their own:
 * `regionWord` of the world's id, FNV-1a over the id's UTF-16 units, so all of them agree without anything on the wire. The
 * turn to pair it with is `turnOf` of the shared game clock: the page's copy of the server's clock (`serverNow`, set by the
 * `welcome`; the page draws no weather before it), the server's own clock on the map server.
 *
 * @param worldId The world's id.
 * @returns An unsigned 32-bit integer, the `worldSeed` of `weatherAt`.
 * @example
 * weatherAt(DEFAULT_WEATHER, worldWeatherSeed("main"), "ashfield", turnOf(now));
 */
export function worldWeatherSeed(worldId: string): number {
  return regionWord(worldId);
}

/**
 * Cuts a turn to a whole number of 0 or more.
 *
 * @param turn A turn that may be negative, fractional or not finite.
 * @returns The whole turn, 0 when it cannot be one.
 */
function wholeTurn(turn: number): number {
  return Number.isFinite(turn) && turn > 0 ? Math.floor(turn) : 0;
}

/**
 * The wetness of a front in basis points of a turn raining.
 *
 * It is drawn uniformly around the season's `rainPercent`, so the average over a season is that percent, and a low draw is a
 * dry spell.
 *
 * @param odds The odds of the season the front begins in.
 * @param draw A uniform draw from 0 to 2^24 - 1.
 * @returns A whole number of basis points from 0 to 10000.
 */
function frontBasisPoints(odds: SeasonOdds, draw: number): number {
  const mean = odds.rainPercent * 100;
  const reach = Math.floor((odds.swingPercent * Math.min(mean, BASIS_POINTS - mean)) / 100);
  return mean - reach + Math.floor((draw * (2 * reach + 1)) / UNIT_RANGE);
}

/**
 * What falls on a region in a turn.
 *
 * Time is cut into fronts of `frontTurns` turns. A front's wetness comes from the hash of the world seed, the region and the
 * front, shaped by the odds of the season the front begins in. A turn rains when its own draw falls under the front's wetness,
 * and a rain in a front at or above the season's storm line is a storm. The same inputs give the same weather on every map
 * server and page; no clock, no random source and no stored state is read.
 *
 * @param weather The weather table.
 * @param worldSeed The world's seed, an unsigned 32-bit integer.
 * @param regionId The region id (`weatherRegionOf`).
 * @param turnIndex The turn from `turnOf`; below 0 or not finite reads as 0.
 * @returns The kind and the front's wetness from 0 to 255.
 * @throws RangeError when the world seed is not an unsigned 32-bit integer.
 * @example
 * weatherAt(DEFAULT_WEATHER, seed, "ashfield", turnOf(Date.now()));
 */
export function weatherAt(weather: WeatherTable, worldSeed: number, regionId: string, turnIndex: number): Weather {
  const turn = wholeTurn(turnIndex);
  const region = regionWord(regionId);
  const frontIndex = Math.floor(turn / weather.frontTurns);
  const odds = weather.seasons[seasonOfTurn(frontIndex * weather.frontTurns)];
  const wetBasisPoints = frontBasisPoints(odds, hash(worldSeed, region, FRONT_DOMAIN, frontIndex) >>> 8);
  const wetness = Math.floor((wetBasisPoints * 255) / BASIS_POINTS);
  const drawBasisPoints = Math.floor(((hash(worldSeed, region, TURN_DOMAIN, turn) >>> 8) * BASIS_POINTS) / UNIT_RANGE);
  if (drawBasisPoints >= wetBasisPoints) return { kind: "clear", wetness };
  return { kind: wetBasisPoints >= odds.stormFrom * 100 ? STORM_KIND : "rain", wetness };
}

/**
 * What falls on a region at a server-clock time.
 *
 * @param weather The weather table.
 * @param worldSeed The world's seed.
 * @param regionId The region id.
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns The weather of the turn the time falls in.
 */
export function weatherAtTime(weather: WeatherTable, worldSeed: number, regionId: string, epochMs: number): Weather {
  return weatherAt(weather, worldSeed, regionId, turnOf(epochMs));
}
